import {
  ArrowDownIcon,
  ArrowUpIcon,
  BorderAllIcon,
  CheckCircledIcon,
  Crosshair2Icon,
  LightningBoltIcon,
} from '@radix-ui/react-icons';
import {
  Badge,
  Card,
  Flex,
  Grid,
  Heading,
  Skeleton,
  Text,
} from '@radix-ui/themes';
import { ErrorBoundary } from 'components/common/error-boundary';
import { MachinePerformanceHelper } from 'lib_ts/classes/machine-performance.helper';
import { BallType } from 'lib_ts/enums/machine.enums';
import {
  MAX_BREAKS_DELTA_IN,
  MAX_BREAKS_DELTA_IN_TRAJEKT_36,
  MAX_PLATE_DELTA_IN,
  MAX_VELO_DELTA_MPH,
} from 'lib_ts/enums/pitch-performance';
import { RADIX } from 'lib_ts/enums/radix-ui';
import {
  IGroupedFires,
  IMetricDetails,
  IMetricTally,
} from 'lib_ts/interfaces/i-pitch-performance';
import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import usePitchPerformanceStore from './store/use-pitch-performance-store';

// Icon colors, some RADIX.COLOR values don't WORK for icons?
const VELO_ICON_HEX = '#FFC53D';
const BREAK_ICON_HEX = '#0090FF';
const PLATE_ICON_HEX = '#E5484D';

const DeltaBadge = ({
  delta,
  hide,
}: {
  delta: number | undefined;
  hide?: boolean;
}) => {
  if (hide || delta === undefined) {
    return null;
  }

  // skip if we'd show a 0 delta
  const noChange = Math.abs(delta).toFixed(0) === '0';
  if (noChange) {
    return null;
  }

  switch (Math.sign(delta)) {
    case 1: {
      return (
        <Badge color={RADIX.COLOR.SUCCESS}>
          <ArrowUpIcon height="12" width="12" />
          {delta.toFixed(0)}%
        </Badge>
      );
    }

    case -1: {
      return (
        <Badge color={RADIX.COLOR.DANGER}>
          <ArrowDownIcon height="12" width="12" />
          {Math.abs(delta).toFixed(0)}%
        </Badge>
      );
    }

    case 0:
    default: {
      return null;
    }
  }
};

export const PitchPerfMetrics = (props: {
  currGroups: IGroupedFires[];
  prevGroups: IGroupedFires[];
  loading: boolean;
}) => {
  const { isAveraged, selectedShotIds, filters } = usePitchPerformanceStore(
    useShallow((state) => ({
      isAveraged: state.isAveraged,
      selectedShotIds: state.selectedShotIds,
      filters: state.filters,
    }))
  );

  const metrics = useMemo(() => {
    let current: IMetricTally;
    let previous: IMetricTally;

    if (selectedShotIds.length > 0) {
      // We're showing the Shot table and the user has selected shots. Only show metrics for those shots.
      current = MachinePerformanceHelper.getTallyFromShotIds(
        props.currGroups,
        selectedShotIds
      );
      previous = MachinePerformanceHelper.getTallyFromShotIds(
        props.prevGroups,
        selectedShotIds
      );
    } else {
      current = isAveraged
        ? MachinePerformanceHelper.getAverageTallyFromGroups(props.currGroups)
        : MachinePerformanceHelper.getTallyFromGroups(props.currGroups);

      previous = isAveraged
        ? MachinePerformanceHelper.getAverageTallyFromGroups(props.prevGroups)
        : MachinePerformanceHelper.getTallyFromGroups(props.prevGroups);
    }

    const deltas: IMetricDetails | undefined =
      current.counts.total === 0
        ? undefined
        : {
            total:
              (100 * (current.counts.total - previous.counts.total)) /
              current.counts.total,
            overall: current.percents.overall - previous.percents.overall,
            velocity: current.percents.velocity - previous.percents.velocity,
            break: current.percents.break - previous.percents.break,
            plate: current.percents.plate - previous.percents.plate,
          };

    return {
      current: current,
      previous: previous,
      deltas: deltas,
    };
  }, [props.currGroups, props.prevGroups, isAveraged, selectedShotIds]);

  const { current, deltas } = metrics;

  const metricTarget = isAveraged ? 'Pitches' : 'Shots';

  const MAX_BREAKS_DELTA_INCHES =
    filters.ball_type === BallType.Trajekt_36
      ? MAX_BREAKS_DELTA_IN_TRAJEKT_36
      : MAX_BREAKS_DELTA_IN;

  return (
    <ErrorBoundary componentName="PitchPerfMetrics">
      <Flex direction="column" gap="3">
        <Heading size="4">Performance Metrics</Heading>

        <Grid columns="4" gap="4">
          <Skeleton loading={props.loading}>
            <Card size="2">
              <Heading size="3">Good {metricTarget}</Heading>
              <Text size="1">{metricTarget} acceptable across all metrics</Text>

              {current && (
                <Flex align="center" gap={RADIX.FLEX.GAP.SM} mt="2">
                  <CheckCircledIcon
                    color={RADIX.COLOR.SUCCESS}
                    height="16"
                    width="16"
                  />
                  <Heading size="6">
                    {current.percents.overall.toFixed(0)}%
                  </Heading>
                  <DeltaBadge
                    delta={deltas?.overall}
                    hide={!!selectedShotIds}
                  />
                </Flex>
              )}
            </Card>
          </Skeleton>
          <Skeleton loading={props.loading}>
            <Card size="2">
              <Heading size="3">Velocity</Heading>
              <Text size="1">
                {metricTarget} within {MAX_VELO_DELTA_MPH}mph of target
              </Text>

              {current && (
                <Flex align="center" gap={RADIX.FLEX.GAP.SM} mt="2">
                  {/* I have no idea why, but RADIX.COLOR.WARNING does not work for icons? */}
                  <LightningBoltIcon
                    color={VELO_ICON_HEX}
                    height="16"
                    width="16"
                  />
                  <Heading size="6">
                    {current.percents.velocity.toFixed(0)}%
                  </Heading>
                  <DeltaBadge
                    delta={deltas?.velocity}
                    hide={!!selectedShotIds}
                  />
                </Flex>
              )}
            </Card>
          </Skeleton>
          <Skeleton loading={props.loading}>
            <Card size="2">
              <Heading size="3">Break</Heading>
              <Text size="1">
                {metricTarget} within {MAX_BREAKS_DELTA_INCHES}" of target
              </Text>

              {current && (
                <Flex align="center" gap={RADIX.FLEX.GAP.SM} mt="2">
                  <BorderAllIcon
                    color={BREAK_ICON_HEX}
                    height="16"
                    width="16"
                  />
                  <Heading size="6">
                    {current.percents.break.toFixed(0)}%
                  </Heading>
                  <DeltaBadge delta={deltas?.break} hide={!!selectedShotIds} />
                </Flex>
              )}
            </Card>
          </Skeleton>
          <Skeleton loading={props.loading}>
            <Card size="2">
              <Heading size="3">Plate Location</Heading>
              <Text size="1">
                {metricTarget} within {MAX_PLATE_DELTA_IN}" of target
              </Text>

              {current && (
                <Flex align="center" gap={RADIX.FLEX.GAP.SM} mt="2">
                  <Crosshair2Icon
                    color={PLATE_ICON_HEX}
                    height="16"
                    width="16"
                  />
                  <Heading size="6">
                    {current.percents.plate.toFixed(0)}%
                  </Heading>
                  <DeltaBadge delta={deltas?.plate} hide={!!selectedShotIds} />
                </Flex>
              )}
            </Card>
          </Skeleton>
        </Grid>
      </Flex>
    </ErrorBoundary>
  );
};
